import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import {type IssueAgentTask, useIssueAgentTasks} from '@github-ui/agent-sessions/hooks/use-issue-agent-tasks'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {getTaskTitle} from '@github-ui/agent-sessions/utils/task-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CopilotIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import styles from './IssueAgentTasksSection.module.css'

type IssueAgentTasksSectionProps = {
  owner: string
  repo: string
  issueNumber: number
  className?: string
  testId?: string
}

/**
 * Renders a compact list of agent tasks for an issue.
 * Placed between the issue body and the sub-issues list in the issue detail view.
 * Only renders when there are tasks associated with the issue.
 */
export function IssueAgentTasksSection({owner, repo, issueNumber, className, testId}: IssueAgentTasksSectionProps) {
  const tasksQuery = useIssueAgentTasks({owner, repo, issueNumber})
  const tasks = tasksQuery.data?.tasks ?? []

  if (!isFeatureEnabled('issue_cca_visualization_session_panel')) return null
  if (tasksQuery.isLoading) {
    return null
  }
  const containerClassName = clsx(styles.container, className)

  if (tasksQuery.error) {
    return (
      <div className={containerClassName} data-testid={testId ?? 'issue-agent-tasks-section'}>
        <div className={styles.taskStatus}>Unable to load Copilot coding agent tasks.</div>
      </div>
    )
  }

  if (tasks.length === 0) {
    return null
  }

  return (
    <div className={containerClassName} data-testid={testId ?? 'issue-agent-tasks-section'}>
      <ul className={styles.taskList}>
        {tasks.map(task => (
          <AgentTaskItem key={task.id} task={task} />
        ))}
      </ul>
    </div>
  )
}

function AgentTaskItem({task}: {task: IssueAgentTask}) {
  const status = isSessionState(task.status) ? task.status : undefined
  const title = getTaskTitle(task)

  return (
    <li className={styles.taskItem}>
      <div className={styles.taskTopRow}>
        <div className={styles.taskTitle}>
          {task.url ? (
            <a href={task.url} className={styles.taskTitleLink}>
              {title}
            </a>
          ) : (
            title
          )}
        </div>

        <div className={styles.taskControls}>
          {status && <TaskLeadingVisual sessionState={status} className={styles.statusIcon} />}
          <CopilotIcon className={styles.agentIcon} />
        </div>
      </div>
    </li>
  )
}
